import { type Moment } from "moment";

import { type UserInfo } from "helpers/AppProvider.types";

import { BTLoginTypes } from "types/enum";

import { formattedPlural } from "utilities/string/string";

import {
    type ISignatureAssignedUser,
    type UserTypeIcon,
} from "commonComponents/entity/media/signatures/common/signatures.type";

import { type Int32BTServiceGroupedListItem } from "handlers";

export const getIconNameForUserType = (userType: BTLoginTypes | undefined): UserTypeIcon => {
    switch (userType) {
        case BTLoginTypes.BUILDER:
            return "InternalUsers";
        case BTLoginTypes.SUBS:
            return "SubsVendors";
        case BTLoginTypes.CONTACT:
            return "Homeowners";
        default:
            return null;
    }
};

export const SignatureLineLabel = "Signature";
export const NameLineLabel = "Name";
export const DateLineLabel = "Date";

export const getFriendlyDueDate = (dueDate?: Moment) => {
    if (!dueDate) {
        return "";
    }

    return dueDate.format("MMMM D, YYYY [at] h:mmA");
};

export const getFriendlyReminder = (reminder?: number | null) => {
    if (!reminder) {
        return "";
    }

    return `${reminder} ${formattedPlural({
        one: "day",
        other: "days",
        value: reminder,
    })} before due date`;
};

export const getSigneeColor = (colorsUsed: string[]): string => {
    // Find the first color not in the colorsUsed array
    for (const color of signeeColors) {
        if (!colorsUsed.includes(color)) {
            return color;
        }
    }

    // If all colors are used, start reusing colors from the beginning
    // Use modulo to cycle through colors based on how many are already used
    const colorIndex = colorsUsed.length % signeeColors.length;
    return signeeColors[colorIndex];
};

export const signeeColors = [
    "#FFD49A",
    "#D1D6FF",
    "#FFCDC1",
    "#FAC2F9",
    "#BCE4FF",
    "#F8DC83",
    "#D3DBE2",
    "#E4CEFD",
    "#AFF2D7",
    "#88FFFF",
];

const createSignatureAssignedUser = (user: UserInfo): ISignatureAssignedUser | null => {
    if (!user.globalUserId || !user.fullName) {
        return null;
    }

    return {
        id: user.globalUserId,
        name: user.fullName,
        loginType: user.loginType,
    };
};

/**
 * Returns the assigned user for a signature request, accounting for users with multi-builder access.
 *
 * @param assignedToList Current list of possible assigned users
 * @param currentUser Current user in context (user who is attempting to sign)
 * @param isExternalUser Is the current user an external user
 *
 * @returns ISignatureAssignedUser with id, name, and loginType when a valid user is found.
 *          Returns null otherwise
 */
export const getAssignedToSignee = (
    assignedToList: Int32BTServiceGroupedListItem[],
    currentUser: UserInfo | undefined,
    isExternalUser: boolean,
): ISignatureAssignedUser | null => {
    if (isExternalUser || !currentUser || assignedToList.length === 0) return null;

    const flattenSelectedAssignedTo =
        assignedToList
            .flatMap((user) => user.options)
            .filter((flattenedUser) => flattenedUser?.selected) ?? [];

    // With multi-builder users, currentUser.globalUserId may not match the assigned user's id.
    const assignedUser = flattenSelectedAssignedTo.find((user) =>
        currentUser.orgLinks?.some((link) => link.orgLinkId === user?.id),
    );

    // Allow the user to sign if they are assigned
    if (assignedUser) {
        return createSignatureAssignedUser({
            globalUserId: assignedUser.id,
            fullName: assignedUser.name,
            loginType: currentUser.loginType,
        } as UserInfo);
    }

    // Allow internal users to manually approve and unassigned users to approve
    return createSignatureAssignedUser(currentUser);
};
